import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  makeStyles,
  Popover,
  ClickAwayListener,
  Typography,
} from "@material-ui/core";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.background.paper,
    border: `2px solid ${grayColor[1]}`,
    borderRadius: theme.spacing(0.75),
    margin: theme.spacing(1.5, 1),
    padding: theme.spacing(0.5),
    height: "48px",
    marginLeft: "auto",
    marginRight: "auto",
    boxSizing: "border-box",
    boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
    overflow: "hidden",
    alignSelf: "center",
    minWidth: "60px",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      transform: "scale(1.05)",
      boxShadow: "0 5px 12px rgba(0,0,0,0.25)",
      zIndex: 10,
    },
  },
  section: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(0.25, 0.5),
    minWidth: "45px",
    textAlign: "center",
    flex: "1 1 auto",
    whiteSpace: "nowrap",
  },
  divider: {
    width: "1px",
    height: "auto",
    backgroundColor: theme.palette.divider,
    alignSelf: "stretch",
    margin: theme.spacing(0, 0.5),
    flexShrink: 0,
  },
  label: {
    padding: theme.spacing(0, 0.25),
    fontSize: "8px",
    fontWeight: "normal",
    color: theme.palette.text.secondary,
    textTransform: "capitalize",
    letterSpacing: "0.2px",
    marginTop: "1px",
    textAlign: "center",
    textWrap: "nowrap",
    lineHeight: 1,
    margin: 0,
  },
  value: {
    fontSize: "12px",
    fontWeight: "bold",
    color: theme.palette.text.primary,
    textAlign: "center",
    lineHeight: 1,
    margin: "0 0 1px 0",
  },
  scrollableContent: {
    maxHeight: "200px",
    overflowY: "auto",
    paddingRight: "4px",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(255, 255, 255, 0.2)",
      borderRadius: "3px",
      "&:hover": {
        background: "rgba(255, 255, 255, 0.4)",
      },
    },
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(255, 255, 255, 0.2) transparent",
  },
}));

const StepBox = ({
  step,
  stepType,
  trackId,
  style,
  isStacked = false,
  isSkeletonMode = false,
  displayModes = ["priceNet", "margin", "taxes", "priceGross"],
  selectedAnchor = "",
  clientColor = "#3498db",
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleClick = (event) => {
    const content = createTooltipContent();
    if (content) {
      setAnchorEl(event.currentTarget);
      setPopoverOpen(true);
    }
  };

  const handleClose = () => {
    setPopoverOpen(false);
    setAnchorEl(null);
  };

  // tooltip title based on step type
  const getStepTitle = () => {
    const titles = {
      step1: "Sellin Details",
      step2: "Sellthrough Details",
      step3: "POS Details",
      step4: "Margin Details",
      step5: "Sellout Details",
    };
    return titles[step.type] || "Step Details";
  };

  const createTooltipContent = () => {
    const details = step.tooltip;
    if (!details?.headers || !details?.rows || details.rows.length === 0) {
      return null;
    }

    const { headers, rows } = details;

    return (
      <div
        style={{
          fontFamily: "'Roboto', Arial, sans-serif",
          minWidth: "320px",
          maxWidth: "420px",
          padding: "12px",
          background: "#555555",
          color: "white",
          borderRadius: "6px",
          fontSize: "11px",
          lineHeight: 1.4,
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        }}
      >
        <div
          style={{
            fontSize: "12px",
            fontWeight: "bold",
            marginBottom: "8px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
            paddingBottom: "6px",
          }}
        >
          {getStepTitle()}
        </div>

        <div className={classes.scrollableContent}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {headers.map((header, index) => (
                  <th
                    key={index}
                    style={{
                      padding: "4px 6px",
                      fontSize: "9px",
                      fontWeight: "500",
                      color: "rgba(255, 255, 255, 0.7)",
                      textAlign: index === 0 ? "left" : "center",
                      borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      title={cellIndex === 0 ? cell : undefined}
                      style={{
                        padding: "3px 6px",
                        fontSize: "10px",
                        color:
                          cellIndex === 0
                            ? "rgba(255, 255, 255, 0.9)"
                            : "white",
                        fontWeight: cellIndex === 0 ? "500" : "bold",
                        textAlign: cellIndex === 0 ? "left" : "center",
                        maxWidth: cellIndex === 0 ? "100px" : "auto",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const tooltipContent = createTooltipContent();

  const getStepContent = () => {
    const labels = step.labels || {};
    const metrics = step.metrics || {};

    const stepConfig = {
      step1: [
        {
          label: labels.discount || "",
          value: metrics.discount,
          isPercentage: true,
          mode: "discount",
        },
        {
          label: labels.taxes || "",
          value: metrics.taxes,
          isPercentage: true,
          mode: "taxes",
        },
        {
          label: labels.priceNet || "",
          value: metrics.priceNet,
          isPercentage: false,
          mode: "priceNet",
        },
        {
          label: labels.priceGross || "",
          value: metrics.priceGross,
          isPercentage: false,
          mode: "priceGross",
        },
      ],
      step2: [
        {
          label: labels.margin || "",
          value: metrics.margin,
          isPercentage: true,
          mode: "margin",
        },
        {
          label: labels.marginTotal || "",
          value: metrics.marginTotal,
          isPercentage: true,
          mode: "marginTotal",
        },
        {
          label: labels.priceNet || "",
          value: metrics.priceNet,
          isPercentage: false,
          mode: "priceNet",
        },
        {
          label: labels.taxes || "",
          value: metrics.taxes,
          isPercentage: true,
          mode: "taxes",
        },
        {
          label: labels.priceGross || "",
          value: metrics.priceGross,
          isPercentage: false,
          mode: "priceGross",
        },
      ],
      step3: [
        {
          label: labels.margin || "",
          value: metrics.margin,
          isPercentage: true,
          mode: "margin",
        },
        {
          label: labels.marginTotal || "",
          value: metrics.marginTotal,
          isPercentage: true,
          mode: "marginTotal",
        },
        {
          label: labels.priceNet || "",
          value: metrics.priceNet,
          isPercentage: false,
          mode: "priceNet",
        },
        {
          label: labels.taxes || "",
          value: metrics.taxes,
          isPercentage: true,
          mode: "taxes",
        },
        {
          label: labels.priceGross || "",
          value: metrics.priceGross,
          isPercentage: false,
          mode: "priceGross",
        },
      ],
      step4: [
        {
          label: labels.chainMargin || "",
          value: metrics.chainMargin,
          isPercentage: true,
        },
      ],
      step5: [
        {
          label: labels.price || "",
          value: metrics.price,
          isPercentage: false,
        },
        {
          label: "Relativity",
          value: step.relativity, // Dynamic relativity calculated by frontend
          isPercentage: true,
          isBold: selectedAnchor === trackId, // Bold if this track is the selected anchor
        },
      ],
    };

    const baseContent = stepConfig[step.type] || [];

    // Filter content based on displayModes for step1, step2 and step3
    if (
      step.type === "step1" ||
      step.type === "step2" ||
      step.type === "step3"
    ) {
      const filteredContent = baseContent.filter(
        (item) => !item.mode || displayModes.includes(item.mode),
      );
      // Return all if no valid modes selected (fallback)
      return filteredContent.length > 0 ? filteredContent : baseContent;
    }

    return baseContent;
  };

  const stepContent = getStepContent();

  const getDynamicWidth = () => {
    const contentCount = stepContent.length;

    if (contentCount === 0) return "60px";

    // Calculate width based on content:
    // - Each section needs ~50px minimum
    // - Dividers add 5px each (there are contentCount - 1 dividers)
    // - Add padding for the container (16px total)
    const sectionWidth = 50;
    const dividerWidth = 5;
    const containerPadding = 16;
    const dividerCount = Math.max(0, contentCount - 1);

    const calculatedWidth =
      sectionWidth * contentCount +
      dividerWidth * dividerCount +
      containerPadding;

    // Set reasonable max widths
    const maxWidth = isStacked ? 180 : 220;
    const minWidth = 60;

    return `${Math.max(minWidth, Math.min(calculatedWidth, maxWidth))}px`;
  };

  const dynamicStyle = {
    ...style,
    width: getDynamicWidth(),
    maxWidth: getDynamicWidth(),
    minWidth: "60px",
    ...(isStacked && {
      margin: "2px 4px",
      height: "36px",
      fontSize: "0.85em",
    }),
  };

  const formatValue = (value, isPercentage) => {
    if (isSkeletonMode) return "";
    if (value === null || value === undefined) return "-";
    if (typeof value !== "number") return value;
    // Use 2 decimal places for money values, 1 decimal place for percentages
    const decimals = isPercentage ? 1 : 2;
    return `${value.toFixed(decimals)}${isPercentage ? "%" : ""}`;
  };

  return (
    <>
      <div
        className={classes.container}
        style={dynamicStyle}
        onClick={handleClick}
      >
        {stepContent.map((item, index) => (
          <React.Fragment key={`${trackId}-${stepType}-${index}`}>
            {index > 0 && stepContent.length > 1 && (
              <div className={classes.divider} />
            )}
            <div className={classes.section}>
              <Typography className={classes.value} variant="body2">
                {formatValue(item.value, item.isPercentage)}
              </Typography>
              <Typography
                className={classes.label}
                variant="caption"
                style={{
                  fontWeight: item.isBold ? "bold" : "normal",
                  color: item.isBold ? clientColor : undefined,
                }}
              >
                {isSkeletonMode ? "" : item.label}
              </Typography>
            </div>
          </React.Fragment>
        ))}
      </div>

      {tooltipContent && (
        <Popover
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          transformOrigin={{ vertical: "bottom", horizontal: "center" }}
          PaperProps={{
            style: {
              backgroundColor: "transparent",
              boxShadow: "none",
              overflow: "visible",
            },
          }}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <div>{tooltipContent}</div>
          </ClickAwayListener>
        </Popover>
      )}
    </>
  );
};

StepBox.propTypes = {
  step: PropTypes.shape({
    type: PropTypes.string.isRequired,
    metrics: PropTypes.shape({
      discount: PropTypes.number,
      price: PropTypes.number,
      priceNet: PropTypes.number,
      priceGross: PropTypes.number,
      taxes: PropTypes.number,
      margin: PropTypes.number,
      marginTotal: PropTypes.number,
      chainMargin: PropTypes.number,
    }),
    labels: PropTypes.shape({
      discount: PropTypes.string,
      price: PropTypes.string,
      priceNet: PropTypes.string,
      margin: PropTypes.string,
      marginTotal: PropTypes.string,
      taxes: PropTypes.string,
      priceGross: PropTypes.string,
      chainMargin: PropTypes.string,
    }),
    tooltip: PropTypes.shape({
      headers: PropTypes.array,
      rows: PropTypes.array,
    }),
    relativity: PropTypes.number, // Dynamic relativity calculated by frontend
  }).isRequired,
  stepType: PropTypes.string.isRequired,
  trackId: PropTypes.string.isRequired,
  style: PropTypes.object,
  isStacked: PropTypes.bool,
  isSkeletonMode: PropTypes.bool,
  displayModes: PropTypes.arrayOf(
    PropTypes.oneOf([
      "priceNet",
      "margin",
      "marginTotal",
      "taxes",
      "priceGross",
      "discount",
    ]),
  ),
  selectedAnchor: PropTypes.string,
  clientColor: PropTypes.string,
};

export default StepBox;
